/**
 * Story card photo placement — the pure half of StoryCardPanel.jsx's
 * "Photo position" editor.
 *
 * Each story card template has a PHOTO BOX (`cardPhotoBox`): the whole
 * card (Spotlight), the top 62% (Split), or the inside of the lime
 * window (Frame). A placement is the carousel's shape, {zoom, x, y}:
 * `zoom` 0 = automatic (object-fit: cover, centred — the old look);
 * `zoom` > 0 = sized by hand, the photo `zoom` box widths wide with its
 * top-left corner (`x`, `y`) box widths from the box's corner. Same
 * units and bounds as lib/carouselLayout.js, so the same PhotoStage and
 * helpers (`manualRect`, `autoRect`, `rectToPlacement`) drive both.
 *
 * The Story (9:16) and Feed (4:5) cards have different shapes, so each
 * keeps its own placement, and so does each template (`placementKey`).
 *
 * Pure and framework-free — node --test frontend/scripts/storyCardLayout.test.mjs
 */
import { autoRect, manualRect } from "./carouselLayout.js";
import { tr } from "./i18n.js";

export const CARD_SIZES = [
  { id: "story", get label() { return tr("Story · 9:16"); } },
  { id: "feed", get label() { return tr("Feed · 4:5"); } },
];

/** Template px scaling: every template was designed on a 1080×1920
 *  canvas; StoryCardPanel's `s1920` rounds `v * h / 1920` the same way. */
export const s1920 = (v, h) => Math.round(v * (h / 1920));

/** Split's photo zone height, as a fraction of the card (`h-[62%]`). */
export const SPLIT_PHOTO_FRACTION = 0.62;

/** Where the template's photo lives, in card px. `pad` is the card's
 *  padding ({top, right, bottom, left}), `w`×`h` the card size. The
 *  Frame box is the INSIDE of its lime border (box-sizing: border-box,
 *  so the border eats into the s(780) height). */
export function cardPhotoBox(template, { w, h, pad }) {
  if (template === "split") {
    return { left: 0, top: 0, width: w, height: h * SPLIT_PHOTO_FRACTION };
  }
  if (template === "frame") {
    const border = s1920(18, h);
    const padX = pad.left + s1920(40, h);
    const top = pad.top + s1920(240, h);
    return {
      left: padX + border,
      top: top + border,
      width: w - 2 * padX - 2 * border,
      height: s1920(780, h) - 2 * border,
    };
  }
  return { left: 0, top: 0, width: w, height: h };
}

/** The automatic placement: object-fit cover, centred. */
export const AUTO_CROP = Object.freeze({ scale: 1, position: "50% 50%", origin: "50% 50%" });

/** True when the placement was sized by hand. */
export const isManual = (p) => !!(p && p.zoom > 0);

/** The photo's rect relative to its box, whichever mode it's in.
 *  `aspect` = photo width / height (needed for the auto rect and for a
 *  manual rect's height; without it a manual height is null). */
export function cardPhotoRect(placement, box, aspect) {
  if (isManual(placement)) return manualRect(placement, box, aspect);
  return autoRect(AUTO_CROP, box, aspect);
}

/** The hand-placed photo's rect for the template to draw, or null for
 *  automatic placement (the template keeps its object-cover <img>). */
export function placedPhoto(placement, box, aspect) {
  return isManual(placement) ? manualRect(placement, box, aspect) : null;
}

/** One placement per template per card size. */
export const placementKey = (template, size) => `${template}:${size}`;
